import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert, AlertTitle, Box, Button, CircularProgress, Stack, Step, StepLabel, Stepper,
    TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SftGlobalData from '../../library/SftGlobalData';
import { webshopTokens, WebshopPasswordField } from './_webshopUi';
import UpdaterLog from './UpdaterLog';

// Password field with its own show/hide state (used for the FTP/DB secrets).
function PwField({ label, value, onChange }) {
    const [show, setShow] = useState(false);
    return (
        <Box sx={{ mb: 1.5 }}>
            <WebshopPasswordField label={label} value={value} onChange={(e) => onChange(e.target.value)} visible={show} onToggle={() => setShow(v => !v)} />
        </Box>
    );
}
PwField.propTypes = { label: PropTypes.node, value: PropTypes.any, onChange: PropTypes.func };

/**
 * Webshop setup wizard — the single entry point to get a shop ready for the
 * admin. Replaces the loose install/update buttons on the Connecties tab.
 *
 * Flow:
 *   0. Status  — read-only probe; pick the path (Updates vs. Volledige install).
 *   1. Gegevens — URL (editable; setUrl) + FTP/DB creds (install path only).
 *   2. Uitvoeren — run install/update, show the step log; jump to the admin.
 *
 * Safety still lives server-side: install.php refuses (code 40) on a populated DB.
 */
export default function WebshopSetupScreen(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { cardBg, borderColor, textColor, mutedText } = webshopTokens(props.theme);

    const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);

    // `?new=1` (from the sidebar's "Webshop toevoegen") forces the create form
    // even when a shop is already selected — that's how you add a SECOND shop
    // (e.g. a subsite in its own folder with its own FTP creds and URL). The
    // backend allows N shops per client: unique on (client_id, db_id, slug),
    // slug = URL host, and FTP/DB credentials hang off each webshop row.
    const forceCreate = searchParams.get('new') === '1';
    const webshopId = props.selectedWebshop?.id;
    const post = useCallback((action, body) => SftGlobalData.post(action, {
        key: props.api, webshop_id: webshopId,
        sftUserId: props.login?.sft_user_id, user: props.login?.login, ...body,
    }), [props.api, webshopId, props.login?.sft_user_id, props.login?.login]);

    const [activeStep, setActiveStep] = useState(0);
    const [mode, setMode] = useState(null); // 'update' | 'install'

    // Onboarding (no lic_clients_webshop row yet): create the row from the URL.
    const [newUrl, setNewUrl] = useState('');
    const [creating, setCreating] = useState(false);
    const [provisioning, setProvisioning] = useState(false);

    // ── Step 0: status probe ────────────────────────────────────────────────
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const probe = useCallback(async () => {
        // Skip while the create form is up — probing the CURRENTLY selected
        // shop says nothing about the one being added.
        if (!webshopId || forceCreate) return;
        setStatusLoading(true);
        try {
            const res = await post('webshop&action=status', {});
            setStatus(res?.success ? res.data : null);
        } catch { setStatus(null); }
        finally { setStatusLoading(false); }
    }, [post, webshopId, forceCreate]);
    useEffect(() => { setStatus(null); setActiveStep(0); setMode(null); probe(); }, [probe]);

    // ── Step 1: data (url + creds) ──────────────────────────────────────────
    const [url, setUrl] = useState('');
    const [ftpHost, setFtpHost] = useState('');
    const [ftpPort, setFtpPort] = useState('21');
    const [ftpUser, setFtpUser] = useState('');
    const [ftpPass, setFtpPass] = useState('');
    // Default to /www: on the shop hosting accounts the FTP login lands in the
    // account root and the DOCROOT is its /www subfolder — installing into '/'
    // puts the bundle outside the webroot (files 404). Matches the old desktop
    // installer's tbFtpFolder default.
    const [ftpDir, setFtpDir] = useState('/www');
    const [dbHost, setDbHost] = useState('');
    const [dbPort, setDbPort] = useState('3306');
    const [dbName, setDbName] = useState('');
    const [dbUser, setDbUser] = useState('');
    const [dbPass, setDbPass] = useState('');
    // Redis rate-limiter — optional, differs per webshop. Empty host = the
    // installer skips it (kan later alsnog via Connecties, write-once).
    const [redisHost, setRedisHost] = useState('');
    const [redisPort, setRedisPort] = useState('6379');
    const [redisPass, setRedisPass] = useState('');
    const [redisWhitelist, setRedisWhitelist] = useState('83.217.66.250, 2a00:1c98:70:b1::50');
    const [dataLoading, setDataLoading] = useState(false);

    const loadData = useCallback(async (forMode) => {
        setUrl(props.selectedWebshop?.display_name || '');
        // The update path only edits/uses the URL — the FTP/DB fields render
        // (and save) exclusively on the install path, so skip the credentials
        // round-trip (server-side decrypt included) there.
        if (forMode !== 'install') return;
        setDataLoading(true);
        try {
            const res = await post('webshop&action=getCredentials', {});
            const ftp = res?.success ? res.data?.ftp : null;
            const db = res?.success ? res.data?.db : null;
            if (ftp) {
                setFtpHost(ftp.host || ''); setFtpPort(String(ftp.port || 21));
                setFtpUser(ftp.username || ''); setFtpPass(ftp.password || '');
                setFtpDir(ftp.directory || '/www');
            }
            if (db) {
                setDbHost(db.host || ''); setDbPort(String(db.port || 3306));
                setDbName(db.name || ''); setDbUser(db.username || ''); setDbPass(db.password || '');
            }
        } catch { /* keep blanks */ }
        finally { setDataLoading(false); }
    }, [post, props.selectedWebshop?.display_name]);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const saveDataAndNext = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const calls = [];
            if (url && url !== (props.selectedWebshop?.display_name || '')) {
                calls.push(post('webshop&action=setUrl', { url }));
            }
            if (mode === 'install') {
                calls.push(post('webshop&action=setFtp', { ftp: {
                    host: ftpHost, port: Number(ftpPort) || 21, username: ftpUser, password: ftpPass, directory: ftpDir,
                } }));
                calls.push(post('webshop&action=setDb', { db: {
                    host: dbHost, port: Number(dbPort) || 3306, name: dbName, username: dbUser, password: dbPass,
                } }));
            }
            const results = await Promise.all(calls);
            const failed = results.find(r => !r || !r.success);
            if (failed) { setSaveError(failed?.message || tr('cfg_setup_save_failed', 'Opslaan mislukt.')); return; }
            if (props.onProvisioned) props.onProvisioned();
            setActiveStep(2);
        } catch (e) {
            setSaveError(e?.message || tr('cfg_setup_save_failed', 'Opslaan mislukt.'));
        } finally {
            setSaving(false);
        }
    };

    const installReady = !!(ftpHost && ftpUser && ftpPass && dbHost && dbUser && dbPass && dbName);
    const dataValid = !!url && (mode === 'update' || installReady);

    // ── Step 2: run ─────────────────────────────────────────────────────────
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setRunning(true);
        setResult(null);
        try {
            const res = await post(
                mode === 'install' ? 'webshop&action=install' : 'webshop&action=update',
                mode === 'install' && redisHost.trim()
                    ? { redis_host: redisHost.trim(), redis_port: Number(redisPort) || 6379, redis_password: redisPass, redis_whitelist: redisWhitelist.trim() }
                    : {}
            );
            // Success puts the step log in res.data; a failed install comes
            // back via ResponseHandler::error with the SAME log under res.dev.
            // Read both so the step list renders instead of a bare "Mislukt".
            const payload = [res?.data, res?.dev, res?.data?.dev]
                .find(p => p && Array.isArray(p.steps));
            setResult(payload
                || { ok: false, steps: [{ step: 'error', msg: res?.message || tr('cfg_setup_failed', 'Mislukt.'), ok: false }] });
            if (res?.success && payload?.ok) {
                if (props.notify) props.notify(mode === 'install' ? tr('cfg_setup_installed', 'Webshop geïnstalleerd') : tr('cfg_setup_updated', 'Webshop bijgewerkt'), 4000, 'top-right', props.theme);
                if (props.onProvisioned) props.onProvisioned();
            }
        } catch (e) {
            setResult({ ok: false, steps: [{ step: 'error', msg: e?.message || tr('cfg_setup_failed', 'Mislukt.'), ok: false }] });
        } finally {
            setRunning(false);
        }
    };

    const chooseMode = (m) => { setMode(m); loadData(m); setActiveStep(1); };

    const field = (label, value, onChange, opts = {}) => (
        <TextField label={label} value={value} onChange={(e) => onChange(e.target.value)}
            size="small" fullWidth type={opts.type || 'text'} sx={{ mb: 1.5 }} />
    );

    // No webshop linked yet (no lic_clients_webshop row) → there's nothing to
    // select in the sidebar, so THIS is the onboarding entry. Before the
    // installer (Status → Gegevens → Uitvoeren) can run, the row must exist.
    // We create it straight from the storefront URL: the nightly `webshop_info`
    // cron can't bootstrap a fresh shop (it only reports AFTER install), so we
    // don't wait for it. Once created, `onProvisioned` reloads the list, the
    // shop auto-selects, and this screen re-renders into the full wizard.
    // PHP 8.5 is a HARD requirement, not a recommendation: the mini-api uses
    // 8.5-only syntax (the pipe operator in lib/Auth/JwtVerifier.php, plus
    // 8.4 asymmetric visibility and 8.3 typed constants). On anything older
    // PHP can't even parse it — every mini-api call 500s and the admin cannot
    // reach the shop at all. Shown on both the create form and the wizard so
    // it's read BEFORE anyone installs.
    const phpRequirementAlert = (
        <Alert severity="error" variant="filled" sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                {tr('cfg_setup_php_title', 'Vereist: PHP 8.5 op de webshop-hosting')}
            </AlertTitle>
            <Typography variant="body2">
                {tr('cfg_setup_php_desc', 'Zet de PHP-versie van deze webshop op 8.5 vóór je installeert of updates uitvoert. Op een oudere versie start de mini-api niet op (elke oproep geeft een serverfout) en kan de admin de webshop niet beheren.')}
            </Typography>
        </Alert>
    );

    // Same normalisation auth applies (scheme + trailing slash off, lowercase);
    // the path IS part of the slug, so shop.be/nl and shop.be/fr are distinct.
    const slugOf = (u) => String(u || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();

    const createShop = async () => {
        const clean = newUrl.trim();
        if (!clean) {
            if (props.notify) props.notify(tr('cfg_setup_new_url_required', 'Vul de webshop-URL in.'), 5000, 'top-right', props.theme);
            return;
        }
        // Creating with an existing slug does NOT add a shop: the backend's
        // ON DUPLICATE KEY UPDATE would silently rewrite that shop's URL
        // instead. Catch it here so a typo can't clobber a live webshop.
        const dupe = (props.availableWebshops || []).find(w => slugOf(w.slug) === slugOf(clean));
        if (dupe) {
            if (props.notify) props.notify(
                tr('cfg_setup_add_duplicate', 'Deze URL is al gekoppeld aan een bestaande webshop') + ': ' + (dupe.display_name || dupe.slug),
                7000, 'top-right', props.theme, 1);
            return;
        }
        setCreating(true);
        try {
            const res = await SftGlobalData.post('webshop&action=create', { key: props.api, url: clean });
            const list = Array.isArray(res?.data) ? res.data : [];
            if (res?.success && list.length > 0) {
                if (props.notify) props.notify(tr('cfg_setup_new_created', 'Webshop aangemaakt.'), 4000, 'top-right', props.theme);
                if (props.onProvisioned) props.onProvisioned();
                // Adding a shop next to existing ones: nothing auto-selects it
                // (App keeps the stored selection while it's still in the list),
                // so switch to the new row ourselves and drop ?new=1 — the
                // wizard then continues for the shop just created.
                if (forceCreate && props.setSelectedWebshop) {
                    const fresh = list.find(w => slugOf(w.slug) === slugOf(clean))
                        || list.slice().sort((a, b) => Number(b.id) - Number(a.id))[0];
                    if (fresh) props.setSelectedWebshop(fresh);
                }
                if (forceCreate) setSearchParams({}, { replace: true });
            } else if (props.notify) {
                props.notify(res?.message || tr('cfg_setup_new_failed', 'Aanmaken mislukt.'), 6000, 'top-right', props.theme);
            }
        } catch (e) {
            if (props.notify) props.notify(e?.message || tr('cfg_setup_new_failed', 'Aanmaken mislukt.'), 6000, 'top-right', props.theme);
        } finally {
            setCreating(false);
        }
    };

    // Fallback for a shop that WAS installed before and whose sync-cron already
    // reported: back-fill the row from lic_clients_webshop_info (legacy path).
    const provisionExisting = async () => {
        setProvisioning(true);
        try {
            const res = await SftGlobalData.post('webshop&action=provision', { key: props.api });
            const list = Array.isArray(res?.data) ? res.data : [];
            if (res?.success && list.length > 0) {
                if (props.notify) props.notify(tr('cfg_provision_ok', 'Webshop gekoppeld.'), 4000, 'top-right', props.theme);
                if (props.onProvisioned) props.onProvisioned();
            } else if (res?.success) {
                if (props.notify) props.notify(tr('cfg_provision_empty', 'Geen webshop gevonden in de sync-gegevens. Controleer of de sync-cron correct draait en probeer opnieuw.'), 7000, 'top-right', props.theme);
            } else if (props.notify) {
                props.notify(res?.message || tr('cfg_provision_failed', 'Koppelen mislukt.'), 6000, 'top-right', props.theme);
            }
        } catch (e) {
            if (props.notify) props.notify(e?.message || tr('cfg_provision_failed', 'Koppelen mislukt.'), 6000, 'top-right', props.theme);
        } finally {
            setProvisioning(false);
        }
    };

    if (!webshopId || forceCreate) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <Typography variant="h5" sx={{ color: textColor, fontWeight: 600, mb: 0.5 }}>
                        {forceCreate ? tr('cfg_setup_add_title', 'Webshop toevoegen') : tr('cfg_setup_title', 'Webshop instellen')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mb: 3, maxWidth: 560 }}>
                        {forceCreate
                            ? tr('cfg_setup_add_desc', 'Voeg een extra webshop toe aan deze klant, bijvoorbeeld een subsite in een eigen map. Ze krijgt haar eigen URL, FTP- en databasegegevens; de bestaande webshops blijven ongewijzigd.')
                            : tr('cfg_setup_new_desc', 'Er is nog geen webshop aan dit account gekoppeld. Geef de URL van de storefront op om te beginnen — daarna volgen de FTP- en databasegegevens en de installatie.')}
                    </Typography>
                    {phpRequirementAlert}
                    <Box sx={{ maxWidth: 460 }}>
                        {field(tr('cfg_setup_field_url', 'Webshop-URL'), newUrl, setNewUrl)}
                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: -0.5, mb: 1.5 }}>
                            {forceCreate
                                ? tr('cfg_setup_add_url_help', 'De basis-URL van deze subsite/webshop, bv. https://mijnshop.be/nl. Het deel na http(s):// moet verschillen van de bestaande webshops.')
                                : tr('cfg_setup_new_url_help', 'De basis-URL van de storefront, bv. https://mijnshop.be')}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Button variant="contained" disabled={creating || !newUrl.trim()} onClick={createShop} sx={{ textTransform: 'none' }}
                                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}>
                                {forceCreate ? tr('cfg_webshop_add', 'Webshop toevoegen') : tr('cfg_setup_new_create', 'Webshop aanmaken')}
                            </Button>
                            {forceCreate && (
                                <Button variant="text" disabled={creating} onClick={() => setSearchParams({}, { replace: true })} sx={{ textTransform: 'none' }}>
                                    {tr('cancel', 'Annuleren')}
                                </Button>
                            )}
                        </Stack>
                    </Box>
                    {/* Legacy back-fill from the sync-cron data — only relevant
                        when NO shop is linked yet, so hidden while adding one. */}
                    {!forceCreate && (
                        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${borderColor}` }}>
                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                {tr('cfg_setup_new_existing_hint', 'Werd deze webshop al eerder geïnstalleerd en meldde de sync-cron zich al? Dan kun je ze rechtstreeks koppelen.')}
                            </Typography>
                            <Button variant="text" size="small" disabled={provisioning} onClick={provisionExisting} sx={{ textTransform: 'none' }}
                                startIcon={provisioning ? <CircularProgress size={16} color="inherit" /> : undefined}>
                                {tr('cfg_provision_secondary', 'Bestaande webshop synchroniseren')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </div>
        );
    }

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                <Typography variant="h5" sx={{ color: textColor, fontWeight: 600, mb: 0.5 }}>{tr('cfg_setup_title', 'Webshop instellen')}</Typography>
                <Typography variant="body2" sx={{ color: mutedText, mb: 3 }}>
                    {props.selectedWebshop?.display_name || props.selectedWebshop?.slug}
                </Typography>

                {phpRequirementAlert}

                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    <Step><StepLabel>{tr('cfg_setup_step_status', 'Status')}</StepLabel></Step>
                    <Step><StepLabel>{tr('cfg_setup_step_data', 'Gegevens')}</StepLabel></Step>
                    <Step><StepLabel>{tr('cfg_setup_step_run', 'Uitvoeren')}</StepLabel></Step>
                </Stepper>

                {/* STEP 0 — status */}
                {activeStep === 0 && (
                    <Box>
                        {statusLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CircularProgress size={20} /><Typography variant="body2">{tr('cfg_setup_checking_status', 'Status controleren…')}</Typography>
                            </Box>
                        ) : (
                            <>
                                {(() => {
                                    const cur = status?.version ? ` · v${status.version}` : '';
                                    let severity = 'warning';
                                    let msg;
                                    if (status?.miniApiOk && status?.updateAvailable) {
                                        severity = 'info';
                                        msg = tr('cfg_setup_status_update', 'Mini-api actief%cur%. Er is een nieuwere versie beschikbaar%ver% — voer updates uit.')
                                            .replace('%cur%', cur)
                                            .replace('%ver%', status.latestVersion ? ` (v${status.latestVersion})` : '');
                                    } else if (status?.miniApiOk) {
                                        severity = 'success';
                                        msg = tr('cfg_setup_status_uptodate', 'Mini-api actief%cur% — alles up-to-date.').replace('%cur%', cur);
                                    } else if (status?.miniApiCrashed) {
                                        // files present, PHP fatals on every request: an update
                                        // cannot repair this -- the shop's error log can
                                        severity = 'error';
                                        msg = tr('cfg_setup_status_crashed', 'Mini-api aanwezig maar crasht (HTTP 500%cur%) — ook de webshop-API zelf faalt. Dit is een fatale PHP-fout op de shop; kijk in server/v1/logs/ERR_<datum>.log en server/v1/config/config.php. Een update lost dit niet op.').replace('%cur%', cur);
                                    } else if (status?.storefrontPresent) {
                                        severity = 'info';
                                        msg = tr('cfg_setup_status_sf_present', 'Storefront aanwezig, mini-api ontbreekt — bestaande shop. Voer updates uit om de mini-api toe te voegen.');
                                    } else if (status) {
                                        msg = tr('cfg_setup_status_new_shop', 'Geen storefront gevonden — dit lijkt een nieuwe/lege shop. Een volledige installatie is nodig.');
                                    } else {
                                        msg = tr('cfg_setup_status_unknown', 'Status onbekend — de shop is (nog) niet bereikbaar.');
                                    }
                                    return <Alert severity={severity} sx={{ mb: 2 }}>{msg}</Alert>;
                                })()}
                                {/* Each path spells out what it DOES to the shop: the labels
                                    alone ("Updates uitvoeren" vs "Volledige installatie") don't
                                    say which one touches the database. */}
                                <Stack spacing={2} sx={{ mb: 1 }}>
                                    <Box>
                                        <Button variant="contained" onClick={() => chooseMode('update')} sx={{ textTransform: 'none' }}>
                                            {tr('cfg_setup_btn_run_updates', 'Updates uitvoeren')}
                                        </Button>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.75, maxWidth: 620 }}>
                                            {tr('cfg_setup_update_hint', 'Veilig voor een bestaande webshop: plaatst of vernieuwt de mini-api en voert de databasemigraties uit. Bestaande producten, instellingen en inhoud blijven behouden. Er worden geen bestanden overschreven behalve die van de update.')}
                                        </Typography>
                                    </Box>
                                    {/* Full install only when no storefront is present — running it on
                                        an existing shop (files already there) would rebuild the DB. */}
                                    {!status?.storefrontPresent && (
                                        <Box>
                                            <Button variant="outlined" color="warning" onClick={() => chooseMode('install')} sx={{ textTransform: 'none' }}>
                                                {tr('cfg_setup_btn_full_install', 'Volledige installatie')}
                                            </Button>
                                            <Typography variant="caption" sx={{ color: mutedText, display: 'block', mt: 0.75, maxWidth: 620 }}>
                                                {tr('cfg_setup_full_install_hint', 'Een volledige installatie bouwt de database opnieuw op — enkel voor een lege shop. De server weigert automatisch als er al data is.')}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box>
                                        <Button variant="text" onClick={probe} sx={{ textTransform: 'none', pl: 0 }}>{tr('cfg_setup_btn_refresh_status', 'Status vernieuwen')}</Button>
                                        <Typography variant="caption" sx={{ color: mutedText, display: 'block', maxWidth: 620 }}>
                                            {tr('cfg_setup_refresh_hint', 'Controleert opnieuw of de storefront en de mini-api bereikbaar zijn. Wijzigt niets aan de webshop.')}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </>
                        )}
                    </Box>
                )}

                {/* STEP 1 — data */}
                {activeStep === 1 && (
                    <Box>
                        {dataLoading && <Box sx={{ mb: 2 }}><CircularProgress size={18} /></Box>}
                        {field(tr('cfg_setup_field_url', 'Webshop-URL'), url, setUrl)}
                        {mode === 'install' && (
                            <>
                                <Typography variant="subtitle2" sx={{ color: textColor, mt: 2, mb: 1 }}>{tr('cfg_setup_section_ftp', 'FTP')}</Typography>
                                {field(tr('cfg_setup_field_host', 'Host'), ftpHost, setFtpHost)}
                                {field(tr('cfg_setup_field_port', 'Poort'), ftpPort, setFtpPort)}
                                {field(tr('cfg_setup_field_user', 'Gebruiker'), ftpUser, setFtpUser)}
                                <PwField label={tr('cfg_setup_field_password', 'Wachtwoord')} value={ftpPass} onChange={setFtpPass} />
                                {field(tr('cfg_setup_field_dir', 'Map'), ftpDir, setFtpDir)}
                                <Typography variant="subtitle2" sx={{ color: textColor, mt: 2, mb: 1 }}>{tr('cfg_setup_section_db', 'Database')}</Typography>
                                {field(tr('cfg_setup_field_host', 'Host'), dbHost, setDbHost)}
                                {field(tr('cfg_setup_field_port', 'Poort'), dbPort, setDbPort)}
                                {field(tr('cfg_setup_field_dbname', 'Databasenaam'), dbName, setDbName)}
                                {field(tr('cfg_setup_field_user', 'Gebruiker'), dbUser, setDbUser)}
                                <PwField label={tr('cfg_setup_field_password', 'Wachtwoord')} value={dbPass} onChange={setDbPass} />
                                <Typography variant="subtitle2" sx={{ color: textColor, mt: 2, mb: 1 }}>{tr('cfg_setup_section_redis', 'Redis rate-limiter (optioneel)')}</Typography>
                                <Typography variant="caption" sx={{ color: mutedText, display: 'block', mb: 1 }}>
                                    {tr('cfg_setup_redis_hint', 'Wordt eenmalig in config.php geschreven. Leeg laten = rate limiter uit; kan later alsnog via Connecties.')}
                                </Typography>
                                {field(tr('cfg_setup_field_host', 'Host'), redisHost, setRedisHost)}
                                {field(tr('cfg_setup_field_port', 'Poort'), redisPort, setRedisPort)}
                                <PwField label={tr('cfg_setup_field_password', 'Wachtwoord')} value={redisPass} onChange={setRedisPass} />
                                {field(tr('cfg_conn_redis_whitelist', 'IP-uitzonderingen (optioneel)'), redisWhitelist, setRedisWhitelist)}
                            </>
                        )}
                        {saveError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{saveError}</Alert>}
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                            <Button onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_previous', 'Vorige')}</Button>
                            <Button variant="contained" disabled={!dataValid || saving} onClick={saveDataAndNext} sx={{ textTransform: 'none' }}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
                                {tr('cfg_setup_btn_save_next', 'Opslaan & verder')}
                            </Button>
                        </Stack>
                    </Box>
                )}

                {/* STEP 2 — run */}
                {activeStep === 2 && (
                    <Box>
                        {!result && !running && (
                            <Alert severity={mode === 'install' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                                {mode === 'install'
                                    ? tr('cfg_setup_run_install_info', 'Volledige installatie wordt uitgevoerd. Bij een shop met bestaande data weigert de server automatisch.')
                                    : tr('cfg_setup_run_update_info', 'Updates worden uitgevoerd (mini-api + migraties). Bestaande data blijft behouden.')}
                            </Alert>
                        )}
                        {running && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <CircularProgress size={20} /><Typography variant="body2">{tr('cfg_setup_running', 'Bezig… dit kan enkele minuten duren.')}</Typography>
                            </Box>
                        )}
                        {result && (
                            <Stack spacing={0.5} sx={{ mb: 2 }}>
                                {(result.steps || []).map((s, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        {s.ok
                                            ? <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                            : <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />}
                                        <Typography variant="body2" sx={{ color: s.ok ? 'success.main' : 'error.main' }}>{s.msg}</Typography>
                                    </Box>
                                ))}
                                <UpdaterLog output={result.output} failed={!result.ok} tr={tr}
                                    notify={props.notify ? (msg, ms) => props.notify(msg, ms, 'top-right', props.theme) : undefined} />
                                <Alert severity={result.ok ? 'success' : 'error'} sx={{ mt: 1 }}>
                                    {result.ok ? tr('cfg_setup_done', 'Klaar.') : tr('cfg_setup_failed', 'Mislukt.')}
                                </Alert>
                            </Stack>
                        )}
                        <Stack direction="row" spacing={1.5}>
                            {!result && <Button onClick={() => setActiveStep(1)} disabled={running} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_previous', 'Vorige')}</Button>}
                            {!result && (
                                <Button variant="contained" color={mode === 'install' ? 'warning' : 'primary'} disabled={running} onClick={run} sx={{ textTransform: 'none' }}>
                                    {mode === 'install' ? tr('cfg_setup_btn_install', 'Installeren') : tr('cfg_setup_btn_run', 'Uitvoeren')}
                                </Button>
                            )}
                            {result && result.ok && (
                                <Button variant="contained" sx={{ textTransform: 'none' }} onClick={() => {
                                    if (props.changeScreen) props.changeScreen('webshop-config');
                                    navigate('/' + props.lang + '/webshop-config');
                                }}>
                                    {tr('cfg_setup_btn_to_webshop', 'Naar webshop')}
                                </Button>
                            )}
                            {result && !result.ok && <Button onClick={() => setResult(null)} sx={{ textTransform: 'none' }}>{tr('cfg_setup_btn_retry', 'Opnieuw')}</Button>}
                        </Stack>
                    </Box>
                )}
            </Box>
        </div>
    );
}
